/**
 * Read-model + filter types for the pay-by-bank-backoffice flow. The response
 * shapes mirror `flows/pay-by-bank-backoffice/API_CONTRACT.md` (PRD §6/§7)
 * verbatim. This is a read-only monitoring surface, so there is no zod form
 * schema (PRD §6) — these types just describe the payloads the screens render
 * and the in-memory filter state that parameterises the list query.
 *
 * Optional fields (`concept`, `paymentType`, `description`, the `customField*`
 * / Volt fields) are omitted from the JSON when empty (backend
 * `@JsonInclude(NON_NULL)`), so they are possibly-undefined here.
 */

/** Revenue lifecycle status from the list `data[].status` (raw, untranslated). */
export type RawStatus = 'UNPAID' | 'PAID' | 'FAILED' | 'REJECTED' | 'CANCELLED';

/** A single row of `GET /payments` (PRD §4b). */
export type PaymentRow = {
  id: string;
  externalClientId: string;
  concept?: string;
  paymentType?: string;
  description?: string;
  clientName: string;
  status: string;
  invoiceAmount: number;
  invoiceCurrency: string;
  requestDate: string;
};

/** `GET /payments` response: aggregates + flat rows (PRD §4c/§6). */
export type PaymentListResponse = {
  total: number;
  totalRecords: number;
  totalAmount: number;
  page: number;
  pageTotal: number;
  hasNext: boolean;
  data: PaymentRow[];
};

/** `GET /payments/{revenueId}` response — consolidated detail (PRD §4d/§7). */
export type PaymentDetailResponse = {
  id: string;
  externalClientId: string;
  concept?: string;
  customField1?: string;
  customField2?: string;
  customField3?: string;
  customField4?: string;
  customField5?: string;
  invoiceAmount: number;
  invoiceCurrency: string;
  paymentStatus: string;
  detailedStatus?: string;
  bankPaymentScheme?: string;
  senderName?: string;
  senderIban?: string;
  receiverName?: string;
  receiverIban?: string;
  result: { code: string };
  /**
   * Header values the detail screen renders. Not in the contract's sample
   * (PRD O-1) — Phase-2 default assumes the response carries them so a deep
   * link works without a borrowed list row. The mock returns them.
   */
  clientName?: string;
  requestDate?: string;
};

/** Date-range preset choices for the list (PRD §4a). */
export type DateRangePreset =
  | 'current-month'
  | 'last-month'
  | 'current-year'
  | 'last-year'
  | 'custom';

/** In-memory list query controls (PRD §4a) — not a zod-validated submission. */
export type PaymentFilters = {
  dateRangePreset: DateRangePreset;
  /** Only meaningful when `dateRangePreset === 'custom'`; ISO `YYYY-MM-DD`. */
  fromDate?: string;
  toDate?: string;
  externalClientId: string;
  clientId: string;
  status: string;
  page: number;
};

/** Default filter state on flow entry (PRD §2/§4a): current month, no filters. */
export const defaultFilters: PaymentFilters = {
  dateRangePreset: 'current-month',
  externalClientId: '',
  clientId: '',
  status: '',
  page: 1,
};
